<template>
  <div class="canvas-controls" data-figma-node="2902:174524" role="toolbar" aria-label="Canvas controls">
    <NewStudioToolButton ref="gridOptionsBtn" class="canvas-controls__grid-options" label="Grid Options" multiline icon="/new-studio/icons/grid-options.svg" :active="showGridOptions" @click="showGridOptions ? closeGridOptions() : actions.gridOptions()" />
    <NewStudioToolButton label="Pan" icon="/new-studio/icons/pan.svg" :active="gridStore.selectedTool === SELECTABLE_TOOLS.MOVE_GRID" :pressed="gridStore.selectedTool === SELECTABLE_TOOLS.MOVE_GRID" @click="actions.pan" />
    <NewStudioToolButton label="Zoom In" icon="/new-studio/icons/zoom-in.svg" @click="actions.zoomIn" />
    <NewStudioToolButton label="Zoom Out" icon="/new-studio/icons/zoom-out.svg" @click="actions.zoomOut" />
    <button
      v-if="is3DGrid"
      type="button"
      class="canvas-controls__view-toggle"
      role="switch"
      :aria-checked="gridStore.showDepthPreview"
      aria-label="Toggle 3D viewing mode"
      data-figma-node="5457:173591"
      @click="gridStore.setShowDepthPreview(!gridStore.showDepthPreview)"
    >
      <span class="canvas-controls__view-toggle-track">
        <span class="canvas-controls__view-toggle-thumb">
          <span class="canvas-controls__view-toggle-thumb-label">{{ gridStore.showDepthPreview ? '3D' : '2D' }}</span>
        </span>
      </span>
      <span class="canvas-controls__view-toggle-caption">View Mode</span>
    </button>
    <NewStudioGridOptionsPopover :open="showGridOptions" :trigger-el="gridOptionsBtnEl" :highlighted-grid-ids="highlightedGridIds" @close="closeGridOptions" />
  </div>
</template>

<script setup lang="ts">
import { SELECTABLE_TOOLS, GRID_PATTERN_IDS } from '~/composables/grid/constants'

const actions = useNewStudioActions()
const gridStore = useGridStore()
const { showGridOptions, highlightedGridIds } = actions
const is3DGrid = computed(() => gridStore.gridPattern?.id === GRID_PATTERN_IDS.THREE_D)
const closeGridOptions = () => {
  showGridOptions.value = false
  highlightedGridIds.value = []
}
onUnmounted(closeGridOptions)
const gridOptionsBtn = useTemplateRef<InstanceType<typeof import('./ToolButton.vue').default>>('gridOptionsBtn')
const gridOptionsBtnEl = computed(() => (gridOptionsBtn.value as any)?.$el as HTMLElement | undefined)
</script>

<style scoped>
.canvas-controls {
  position: absolute;
  z-index: 4;
  top: 26px;
  left: 26px;
  display: flex;
  width: 88px;
  height: 431px;
  flex-direction: column;
  gap: 8px;
  pointer-events: auto;
}

.canvas-controls :deep(.studio-tool) {
  width: 88px;
  height: 76px;
  padding: 14px 6px;
  border: 1px solid var(--studio-border);
  border-radius: 8px;
  background: var(--studio-surface);
  box-shadow: var(--studio-card-shadow);
}

.canvas-controls :deep(.canvas-controls__grid-options) {
  height: 87px;
}

.canvas-controls :deep(.canvas-controls__grid-options .studio-tool__label) {
  max-width: 48px;
  line-height: 13px;
}

.canvas-controls__view-toggle {
  display: flex;
  width: 88px;
  height: 84px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 13px 5px;
  box-sizing: border-box;
  border: 1px solid var(--studio-border);
  border-radius: 8px;
  background: var(--studio-surface);
  box-shadow: var(--studio-card-shadow);
  font: inherit;
  cursor: pointer;
}

.canvas-controls__view-toggle-track {
  position: relative;
  display: block;
  width: 72px;
  height: 37px;
  border: 1.3px solid #e2e1dc;
  border-radius: 14.5px;
  background-color: #e2e1dc;
  transition: background-color 0.2s;
}

.canvas-controls__view-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 29px;
  height: 29px;
  padding: 7px 5px;
  box-sizing: border-box;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}

.canvas-controls__view-toggle[aria-checked="true"] .canvas-controls__view-toggle-track {
  background-color: #313558;
  border-color: #313558;
}

.canvas-controls__view-toggle[aria-checked="true"] .canvas-controls__view-toggle-thumb {
  transform: translateX(35px);
}

.canvas-controls__view-toggle-thumb-label {
  font-family: inherit;
  font-size: 15px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.085px;
  color: var(--studio-text);
}

.canvas-controls__view-toggle-caption {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 13px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: var(--studio-font-semibold);
  line-height: 1;
  color: var(--studio-text);
}

.canvas-controls__view-toggle:focus-visible {
  outline: 3px solid var(--studio-focus-ring);
  outline-offset: 2px;
}
</style>
